import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { invokeEventDecline } from "@/api/messaging";
import { Button } from "@/components/ui/button";

type Status = "confirm" | "loading" | "done" | "error" | "invalid";

export default function NotThisTime() {
  const [searchParams] = useSearchParams();
  const quizrunId = Number(searchParams.get("q"));
  const email = searchParams.get("e") ?? "";
  const token = searchParams.get("t") ?? "";
  // Only decline on an explicit click: link scanners that open the page must
  // not opt the recipient out.
  const [status, setStatus] = useState<Status>(
    quizrunId > 0 && email && token ? "confirm" : "invalid",
  );
  const [title, setTitle] = useState<string | null>(null);

  const confirm = async () => {
    setStatus("loading");
    try {
      const { data, error } = await invokeEventDecline(quizrunId, email, token);
      if (error || !data?.ok) {
        setStatus("error");
        return;
      }
      setTitle(data.title);
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16 max-w-md text-center">
        {status === "confirm" && (
          <div className="flex flex-col items-center gap-4">
            <h1 className="text-xl font-bold">Not this time?</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We'll stop sending invites and reminders about this month's Quizrun
              to {email && <span className="font-medium">{email}</span>}. You'll
              still hear about the next one.
            </p>
            <Button onClick={confirm}>Not this time</Button>
            <Link to="/events" className="text-sm underline text-muted-foreground">
              Register anyway
            </Link>
          </div>
        )}

        {status === "loading" && (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin" />
            <p>Saving…</p>
          </div>
        )}

        {status === "done" && (
          <div className="flex flex-col items-center gap-3">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
            <h1 className="text-xl font-bold">Got it — see you next time</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              No more reminders about {title ? <span className="font-medium">{title}</span> : "this Quizrun"}.
            </p>
            <Link to="/events" className="text-sm underline text-muted-foreground">
              Changed your mind? Register anyway
            </Link>
          </div>
        )}

        {status === "invalid" && (
          <div className="flex flex-col items-center gap-3">
            <XCircle className="w-10 h-10 text-red-500" />
            <h1 className="text-xl font-bold">Invalid link</h1>
            <p className="text-sm text-muted-foreground">
              This link is missing information. Please use the "Not this time"
              link from one of our invites.
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center gap-3">
            <XCircle className="w-10 h-10 text-red-500" />
            <h1 className="text-xl font-bold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              We couldn't save that. Please try the link again, or just ignore
              the reminders.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
